import { FC, useRef, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import LPContainer from '../LandingPage/LPContainer'

const getPhases = (t: (key: string) => string) => [
  {
    title: t('lp:talent_ai:questions:understand:title'),
    description: t('lp:talent_ai:questions:understand:description'),
    items: [
      t('lp:talent_ai:questions:understand:item1'),
      t('lp:talent_ai:questions:understand:item2'),
      t('lp:talent_ai:questions:understand:item3')
    ]
  },
  {
    title: t('lp:talent_ai:questions:recommend:title'),
    description: t('lp:talent_ai:questions:recommend:description'),
    items: [
      t('lp:talent_ai:questions:recommend:item1'),
      t('lp:talent_ai:questions:recommend:item2'),
      t('lp:talent_ai:questions:recommend:item3')
    ]
  },
  {
    title: t('lp:talent_ai:questions:action:title'),
    description: t('lp:talent_ai:questions:action:description'),
    items: [
      t('lp:talent_ai:questions:action:item1'),
      t('lp:talent_ai:questions:action:item2'),
      t('lp:talent_ai:questions:action:item3')
    ]
  }
]

const Dot = () => {
  return (
    <div className="absolute left-0 top-0 w-6 h-6 -translate-x-[11px] bg-transparent rounded-full flex items-center justify-center z-10">
      <div className="w-[7px] h-[7px] bg-gray-600 rounded-full"></div>
    </div>
  )
}

const ListPhaseDesktop = ({
  title,
  description,
  items
}: {
  title: string
  description: string
  items: string[]
}) => {
  return (
    <div className="relative pl-4 flex">
      <div className="flex gap-2 tablet:w-[170px] flex-shrink-0">
        <Dot />
        <h3 className="text-lg font-semibold text-gray-700">{title}</h3>
      </div>
      <div className="flex flex-col gap-2">
        <p className="text-gray-900 font-semibold text-base mb-2">
          {description}
        </p>
        <ul className="text-gray-700">
          {items.map((item, index) => (
            <li key={`phase-${index}`} className="flex items-start">
              <span className="text-gray-700 mr-2">•</span>
              <span className="text-base font-medium">{item}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  )
}

const ListPhaseMobile = ({
  title,
  description,
  items
}: {
  title: string
  description: string
  items: string[]
}) => {
  return (
    <div className="relative pl-3">
      <Dot />
      <h3 className="text-base font-semibold text-gray-700">{title}</h3>
      <p className="text-gray-900 font-semibold text-sm my-1.5">
        {description}
      </p>
      <ul className="text-gray-700">
        {items.map((item, index) => (
          <li key={`phase-${index}`} className="flex items-start">
            <span className="text-gray-700 mr-2">•</span>
            <span className="text-sm font-medium">{item}</span>
          </li>
        ))}
      </ul>
    </div>
  )
}

const QuestionsToActionsSection: FC = () => {
  const { t } = useTranslation()
  const mobileContainerRef = useRef<HTMLDivElement>(null)
  const desktopContainerRef = useRef<HTMLDivElement>(null)
  const mobileLastPhaseRef = useRef<HTMLDivElement>(null)
  const desktopLastPhaseRef = useRef<HTMLDivElement>(null)
  const [mobileBottomValue, setMobileBottomValue] = useState<string>('122px')
  const [desktopBottomValue, setDesktopBottomValue] = useState<string>('108px')

  const phases = getPhases(t)

  useEffect(() => {
    const updateBottomValue = () => {
      // Update mobile bottom value
      if (mobileContainerRef.current && mobileLastPhaseRef.current) {
        const containerRect = mobileContainerRef.current.getBoundingClientRect()
        const phaseRect = mobileLastPhaseRef.current.getBoundingClientRect()
        // DOT is at top-0 of the phase, calculate distance from container top to phase top
        const distanceFromTop = phaseRect.top - containerRect.top
        const containerHeight = containerRect.height
        // Line starts at 10px from top, so bottom = containerHeight - distanceFromTop
        const bottom = containerHeight - distanceFromTop - 8
        setMobileBottomValue(`${bottom}px`)
      }

      // Update desktop bottom value
      if (desktopContainerRef.current && desktopLastPhaseRef.current) {
        const containerRect =
          desktopContainerRef.current.getBoundingClientRect()
        const phaseRect = desktopLastPhaseRef.current.getBoundingClientRect()
        const distanceFromTop = phaseRect.top - containerRect.top
        const containerHeight = containerRect.height
        const bottom = containerHeight - distanceFromTop - 8
        setDesktopBottomValue(`${bottom}px`)
      }
    }

    // Use setTimeout to ensure DOM is fully rendered
    const timeoutId = setTimeout(updateBottomValue, 0)
    window.addEventListener('resize', updateBottomValue)

    // Also observe for content changes
    const observer = new ResizeObserver(updateBottomValue)
    if (mobileContainerRef.current) observer.observe(mobileContainerRef.current)
    if (desktopContainerRef.current)
      observer.observe(desktopContainerRef.current)

    return () => {
      clearTimeout(timeoutId)
      window.removeEventListener('resize', updateBottomValue)
      observer.disconnect()
    }
  }, [phases])

  return (
    <div className="relative pt-20 tablet:pt-16 bg-white">
      <div className="hidden tablet:block absolute left-[50%] translate-x-[24px] top-0 bottom-0 w-px border-l"></div>
      <div className="hidden tablet:block absolute left-[50%] translate-x-[194px] top-0 bottom-0 w-px border-l"></div>
      <LPContainer>
        <div className="max-w-7xl mx-auto">
          {/* Tag and Title Section */}
          <div className="text-center mb-8 tablet:mb-0 tablet:text-left">
            <h2 className="text-[31px] leading-[34px] tablet:text-[37px] tablet:leading-[42px] font-semibold text-gray-900 mb-4">
              {t('lp:talent_ai:questions:title')}
            </h2>
            <p className="text-[16px] font-medium tablet:text-lg tablet:mx-0 text-gray-700 max-w-2xl mx-auto">
              {t('lp:talent_ai:questions:subtitle')}
            </p>
          </div>

          {/* Main Content: 2 Column Layout */}
          <div className="grid tablet:grid-cols-2 tablet:gap-12 items-start">
            {/* Left: Banner Image */}

            <picture>
              <source
                media="(min-width: 768px)"
                srcSet="/img/feature/lp/m-question-banner.png"
              />
              <source
                media="(max-width: 767px)"
                srcSet="/img/feature/lp/m-question-banner.png"
              />
              <img
                alt="Turn questions into clear actions"
                loading="lazy"
                className="w-full h-auto"
              />
            </picture>

            {/* Right: Three Phases */}
            {/* Mobile Version */}
            <div className="relative tablet:hidden">
              <div className="relative space-y-8" ref={mobileContainerRef}>
                {/* Continuous vertical line from Understand title to Action title */}
                <div
                  className="absolute left-0 top-[10px] w-px bg-gray-700"
                  style={{ bottom: mobileBottomValue }}></div>
                {phases.map((phase, index) => (
                  <div
                    key={phase.title}
                    ref={
                      index === phases.length - 1 ? mobileLastPhaseRef : null
                    }>
                    <ListPhaseMobile {...phase} />
                  </div>
                ))}
              </div>
            </div>
            {/* Desktop Version */}
            <div className="hidden tablet:block">
              <div className="relative space-y-8" ref={desktopContainerRef}>
                {/* Continuous vertical line from Understand title to Action title */}
                <div
                  className="absolute left-0 top-[10px] w-px bg-gray-700"
                  style={{ bottom: desktopBottomValue }}></div>
                {phases.map((phase, index) => (
                  <div
                    key={phase.title}
                    ref={
                      index === phases.length - 1 ? desktopLastPhaseRef : null
                    }>
                    <ListPhaseDesktop {...phase} />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </LPContainer>
    </div>
  )
}

export default QuestionsToActionsSection
